/**
 * EffectsManager Service
 * 
 * Manages the lifecycle of audio effects processing including:
 * - WebAssembly module loading and caching
 * - Audio Worklet registration and node management
 * - Effects chain management per instrument row
 * - Parameter updates and effect bypass/removal
 */

/**
 * Import types from centralized types file
 */
import type { 
  EffectType, 
  EffectInstance, 
  EffectParameterDefinition, 
  EffectDefinition,
  EffectCategory
} from '../../types/effects';

// Re-export types for backward compatibility
export type { 
  EffectType, 
  EffectInstance, 
  EffectParameterDefinition, 
  EffectDefinition,
  EffectCategory
};

/**
 * Effect registry containing all available effects
 */
const EFFECT_REGISTRY: Record<EffectType, EffectDefinition> = {
  bitcrusher: {
    type: 'bitcrusher',
    name: 'Bit Crusher',
    description: 'Lo-fi digital distortion with sample rate and bit depth reduction',
    category: 'Distortion',
    parameters: [
      {
        id: 'sampleRate',
        name: 'Sample Rate',
        min: 1000,
        max: 48000,
        default: 8000,
        step: 100,
        unit: 'Hz'
      },
      {
        id: 'bitDepth',
        name: 'Bit Depth',
        min: 1,
        max: 16,
        default: 4,
        step: 1,
        unit: 'bits'
      }
    ],
    wasmModule: '/wasm/bitcrusher.wasm'
  },
  reverb: {
    type: 'reverb',
    name: 'Reverb',
    description: 'Algorithmic reverb for spatial depth and ambience',
    category: 'Time-Based',
    parameters: [
      {
        id: 'roomSize',
        name: 'Room Size',
        min: 0.1,
        max: 10.0,
        default: 2.0,
        step: 0.1,
        unit: 's'
      },
      {
        id: 'damping',
        name: 'Damping',
        min: 0,
        max: 100,
        default: 70,
        step: 1,
        unit: '%'
      },
      {
        id: 'preDelay',
        name: 'Pre-Delay',
        min: 0,
        max: 100,
        default: 0,
        step: 1,
        unit: 'ms'
      },
      {
        id: 'mix',
        name: 'Dry/Wet',
        min: 0,
        max: 100,
        default: 30,
        step: 1,
        unit: '%'
      }
    ],
    wasmModule: '/wasm/reverb.wasm'
  },
  delay: {
    type: 'delay',
    name: 'Delay',
    description: 'Echo effect with feedback and filtering',
    category: 'Time-Based',
    parameters: [
      {
        id: 'delayTime',
        name: 'Delay Time',
        min: 1,
        max: 2000,
        default: 250,
        step: 1,
        unit: 'ms'
      },
      {
        id: 'feedback',
        name: 'Feedback',
        min: 0,
        max: 95,
        default: 40,
        step: 1,
        unit: '%'
      },
      {
        id: 'filterFreq',
        name: 'Filter',
        min: 200,
        max: 8000,
        default: 4000,
        step: 10,
        unit: 'Hz'
      },
      {
        id: 'mix',
        name: 'Dry/Wet',
        min: 0,
        max: 100,
        default: 30,
        step: 1,
        unit: '%'
      }
    ],
    wasmModule: '/wasm/delay.wasm'
  },
  overdrive: {
    type: 'overdrive',
    name: 'Overdrive',
    description: 'Warm analog-style distortion with harmonic saturation',
    category: 'Distortion',
    parameters: [
      {
        id: 'drive',
        name: 'Drive',
        min: 0,
        max: 100,
        default: 50,
        step: 1,
        unit: ''
      },
      {
        id: 'tone',
        name: 'Tone',
        min: 0,
        max: 100,
        default: 50,
        step: 1,
        unit: ''
      },
      {
        id: 'outputLevel',
        name: 'Output',
        min: -12,
        max: 12,
        default: 0,
        step: 0.1,
        unit: 'dB'
      }
    ],
    wasmModule: '/wasm/overdrive.wasm'
  },
  // DISABLED: Chorus has audio artifacts that need investigation
  // chorus: {
  //   type: 'chorus',
  //   name: 'Chorus',
  //   description: 'Modulation effect for width and thickness',
  //   category: 'Modulation',
  //   parameters: [
  //     {
  //       id: 'rate',
  //       name: 'Rate',
  //       min: 0.1,
  //       max: 10,
  //       default: 0.8,
  //       step: 0.1,
  //       unit: 'Hz'
  //     },
  //     {
  //       id: 'depth',
  //       name: 'Depth',
  //       min: 0,
  //       max: 100,
  //       default: 25,
  //       step: 1,
  //       unit: ''
  //     },
  //     {
  //       id: 'delay',
  //       name: 'Delay',
  //       min: 10,
  //       max: 50,
  //       default: 15,
  //       step: 1,
  //       unit: 'ms'
  //     },
  //     {
  //       id: 'mix',
  //       name: 'Dry/Wet',
  //       min: 0,
  //       max: 100,
  //       default: 35,
  //       step: 1,
  //       unit: '%'
  //     }
  //   ],
  //   wasmModule: '/wasm/chorus.wasm'
  // },
  eq: {
    type: 'eq',
    name: 'Parametric EQ',
    description: '3-band parametric equalizer for frequency shaping',
    category: 'Dynamics',
    parameters: [
      {
        id: 'lowFreq',
        name: 'Low Freq',
        min: 20,
        max: 500,
        default: 100,
        step: 1,
        unit: 'Hz'
      },
      {
        id: 'lowGain',
        name: 'Low Gain',
        min: -24,
        max: 24,
        default: 0,
        step: 0.1,
        unit: 'dB'
      },
      {
        id: 'lowQ',
        name: 'Low Q',
        min: 0.1,
        max: 10,
        default: 1.0,
        step: 0.1,
        unit: ''
      },
      {
        id: 'midFreq',
        name: 'Mid Freq',
        min: 200,
        max: 8000,
        default: 1000,
        step: 10,
        unit: 'Hz'
      },
      {
        id: 'midGain',
        name: 'Mid Gain',
        min: -24,
        max: 24,
        default: 0,
        step: 0.1,
        unit: 'dB'
      },
      {
        id: 'midQ',
        name: 'Mid Q',
        min: 0.1,
        max: 10,
        default: 1.0,
        step: 0.1,
        unit: ''
      },
      {
        id: 'highFreq',
        name: 'High Freq',
        min: 2000,
        max: 20000,
        default: 8000,
        step: 10,
        unit: 'Hz'
      },
      {
        id: 'highGain',
        name: 'High Gain',
        min: -24,
        max: 24,
        default: 0,
        step: 0.1,
        unit: 'dB'
      },
      {
        id: 'highQ',
        name: 'High Q',
        min: 0.1,
        max: 10,
        default: 1.0,
        step: 0.1,
        unit: ''
      }
    ],
    wasmModule: '/wasm/eq.wasm'
  },
  compressor: {
    type: 'compressor',
    name: 'Compressor',
    description: 'Dynamics processor for controlling dynamic range and adding punch',
    category: 'Dynamics',
    parameters: [
      {
        id: 'threshold',
        name: 'Threshold',
        min: -60,
        max: 0,
        default: -20,
        step: 0.1,
        unit: 'dB'
      },
      {
        id: 'ratio',
        name: 'Ratio',
        min: 1,
        max: 20,
        default: 4,
        step: 0.1,
        unit: ':1'
      },
      {
        id: 'attack',
        name: 'Attack',
        min: 0.1,
        max: 100,
        default: 10,
        step: 0.1,
        unit: 'ms'
      },
      {
        id: 'release',
        name: 'Release',
        min: 10,
        max: 1000,
        default: 100,
        step: 1,
        unit: 'ms'
      },
      {
        id: 'makeup',
        name: 'Makeup Gain',
        min: 0,
        max: 24,
        default: 0,
        step: 0.1,
        unit: 'dB'
      },
      {
        id: 'mix',
        name: 'Mix',
        min: 0,
        max: 100,
        default: 100,
        step: 1,
        unit: '%'
      }
    ],
    wasmModule: '/wasm/compressor.wasm'
  },
  flanger: {
    type: 'flanger',
    name: 'Flanger',
    description: 'Modulation effect creating sweeping, metallic sounds',
    category: 'Modulation',
    parameters: [
      {
        id: 'rate',
        name: 'Rate',
        min: 0.01,
        max: 10,
        default: 0.5,
        step: 0.01,
        unit: 'Hz'
      },
      {
        id: 'depth',
        name: 'Depth',
        min: 0,
        max: 100,
        default: 50,
        step: 1,
        unit: ''
      },
      {
        id: 'delay',
        name: 'Delay',
        min: 0.1,
        max: 10,
        default: 2,
        step: 0.1,
        unit: 'ms'
      },
      {
        id: 'feedback',
        name: 'Feedback',
        min: -95,
        max: 95,
        default: 50,
        step: 1,
        unit: '%'
      },
      {
        id: 'mix',
        name: 'Mix',
        min: 0,
        max: 100,
        default: 50,
        step: 1,
        unit: '%'
      }
    ],
    wasmModule: '/wasm/flanger.wasm'
  },
  tremolo: {
    type: 'tremolo',
    name: 'Tremolo',
    description: 'Amplitude modulation for rhythmic volume pulsing',
    category: 'Modulation',
    parameters: [
      {
        id: 'rate',
        name: 'Rate',
        min: 0.1,
        max: 20,
        default: 4,
        step: 0.1,
        unit: 'Hz'
      },
      {
        id: 'depth',
        name: 'Depth',
        min: 0,
        max: 100,
        default: 50,
        step: 1,
        unit: '%'
      },
      {
        id: 'waveform',
        name: 'Waveform',
        min: 0,
        max: 2,
        default: 0,
        step: 1,
        unit: ''
      },
      {
        id: 'phase',
        name: 'Phase',
        min: 0,
        max: 180,
        default: 0,
        step: 1,
        unit: '°'
      }
    ],
    wasmModule: '/wasm/tremolo.wasm'
  },
  pingpong: {
    type: 'pingpong',
    name: 'Ping-Pong Delay',
    description: 'Stereo delay with alternating left-right echoes',
    category: 'Time-Based',
    parameters: [
      {
        id: 'delayTime',
        name: 'Delay Time',
        min: 1,
        max: 2000,
        default: 250,
        step: 1,
        unit: 'ms'
      },
      {
        id: 'feedback',
        name: 'Feedback',
        min: 0,
        max: 95,
        default: 40,
        step: 1,
        unit: '%'
      },
      {
        id: 'stereoWidth',
        name: 'Stereo Width',
        min: 0,
        max: 100,
        default: 100,
        step: 1,
        unit: '%'
      },
      {
        id: 'filterFreq',
        name: 'Filter',
        min: 200,
        max: 8000,
        default: 4000,
        step: 10,
        unit: 'Hz'
      },
      {
        id: 'mix',
        name: 'Mix',
        min: 0,
        max: 100,
        default: 30,
        step: 1,
        unit: '%'
      }
    ],
    wasmModule: '/wasm/pingpong_delay.wasm'
  },
  autowah: {
    type: 'autowah',
    name: 'Auto-Wah',
    description: 'Envelope-following filter for dynamic, expressive sweeps',
    category: 'Filter',
    parameters: [
      {
        id: 'sensitivity',
        name: 'Sensitivity',
        min: 0,
        max: 100,
        default: 50,
        step: 1,
        unit: ''
      },
      {
        id: 'minFreq',
        name: 'Min Freq',
        min: 200,
        max: 8000,
        default: 500,
        step: 10,
        unit: 'Hz'
      },
      {
        id: 'maxFreq',
        name: 'Max Freq',
        min: 200,
        max: 8000,
        default: 3000,
        step: 10,
        unit: 'Hz'
      },
      {
        id: 'resonance',
        name: 'Resonance',
        min: 0,
        max: 100,
        default: 70,
        step: 1,
        unit: ''
      },
      {
        id: 'attack',
        name: 'Attack',
        min: 1,
        max: 100,
        default: 10,
        step: 0.1,
        unit: 'ms'
      },
      {
        id: 'release',
        name: 'Release',
        min: 10,
        max: 1000,
        default: 200,
        step: 1,
        unit: 'ms'
      },
      {
        id: 'mix',
        name: 'Mix',
        min: 0,
        max: 100,
        default: 100,
        step: 1,
        unit: '%'
      }
    ],
    wasmModule: '/wasm/auto_wah.wasm'
  },
  none: {
    type: 'none',
    name: 'Empty',
    description: 'Empty effect slot',
    category: 'None',
    parameters: []
  }
};

/**
 * EffectsManager manages audio effects processing for all instrument rows
 */
export class EffectsManager {
  private workletNodes: Map<number, AudioWorkletNode>;
  private wasmModules: Map<EffectType, WebAssembly.Module>;
  private audioContext: AudioContext | null;
  private isWorkletRegistered: boolean;
  private initializationPromise: Promise<void> | null;
  private parameterDebounceTimers: Map<string, NodeJS.Timeout>;

  constructor() {
    this.workletNodes = new Map();
    this.wasmModules = new Map();
    this.audioContext = null;
    this.isWorkletRegistered = false;
    this.initializationPromise = null;
    this.parameterDebounceTimers = new Map();
  }

  /**
   * Initialize the effects manager with a native AudioContext
   * Registers the audio worklet processor
   * @param audioContext - Native AudioContext to use for effects processing
   */
  async initialize(audioContext: AudioContext): Promise<void> {
    // Return existing initialization promise if already initializing
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initialize(audioContext);
    return this.initializationPromise;
  }

  private async _initialize(audioContext: AudioContext): Promise<void> {
    try {
      // Store the native AudioContext directly
      this.audioContext = audioContext;
      
      console.log('[EffectsManager] Initializing with native AudioContext:', {
        contextType: audioContext.constructor.name,
        state: audioContext.state,
        sampleRate: audioContext.sampleRate
      });

      // Register audio worklet if not already registered
      if (!this.isWorkletRegistered) {
        await this.registerAudioWorklet();
        this.isWorkletRegistered = true;
      }
    } catch (error) {
      console.error('Failed to initialize EffectsManager:', error);
      throw error;
    }
  }

  /**
   * Get effect definition from registry
   */
  getEffectDefinition(type: EffectType): EffectDefinition | undefined {
    return EFFECT_REGISTRY[type];
  }

  /**
   * Get all available effect definitions
   */
  getAllEffectDefinitions(): EffectDefinition[] {
    return Object.values(EFFECT_REGISTRY).filter(def => def.type !== 'none');
  }

  /**
   * Get effects organized by category
   */
  getEffectsByCategory(): Record<string, EffectDefinition[]> {
    const byCategory: Record<string, EffectDefinition[]> = {};
    
    Object.values(EFFECT_REGISTRY).forEach(def => {
      if (def.type === 'none') return;
      
      if (!byCategory[def.category]) {
        byCategory[def.category] = [];
      }
      const categoryArray = byCategory[def.category];
      if (categoryArray) {
        categoryArray.push(def);
      }
    });
    
    return byCategory;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    // Clear all debounce timers
    this.parameterDebounceTimers.forEach(timer => clearTimeout(timer));
    this.parameterDebounceTimers.clear();

    // Dispose all worklet nodes
    this.workletNodes.forEach((_node, row) => {
      this.disposeWorkletNode(row);
    });
    this.workletNodes.clear();

    // Clear WASM module cache
    this.wasmModules.clear();

    this.audioContext = null;
    this.initializationPromise = null;
  }

  /**
   * Load WebAssembly module for an effect type
   * Fetches, compiles, and caches the WASM module
   */
  async loadWasmModule(effectType: EffectType): Promise<WebAssembly.Module | null> {
    // Check cache first
    if (this.wasmModules.has(effectType)) {
      return this.wasmModules.get(effectType)!;
    }

    const effectDef = this.getEffectDefinition(effectType);
    if (!effectDef || !effectDef.wasmModule) {
      console.warn(`No WASM module defined for effect type: ${effectType}`);
      return null;
    }

    try {
      // Fetch WASM binary from public directory
      const response = await fetch(effectDef.wasmModule);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch WASM module: ${response.statusText}`);
      }

      const wasmBuffer = await response.arrayBuffer();

      // Compile WebAssembly module
      const module = await WebAssembly.compile(wasmBuffer);

      // Cache the compiled module
      this.wasmModules.set(effectType, module);

      console.log(`Successfully loaded WASM module for ${effectType}`);
      return module;
    } catch (error) {
      console.error(`Failed to load WASM module for ${effectType}:`, error);
      
      // Return null to trigger fallback to JavaScript implementation
      return null;
    }
  }

  /**
   * Preload WASM modules for all effects that have them
   * This can be called during app initialization to improve performance
   */
  async preloadWasmModules(): Promise<void> {
    const loadPromises: Promise<WebAssembly.Module | null>[] = [];

    Object.values(EFFECT_REGISTRY).forEach(def => {
      if (def.wasmModule) {
        loadPromises.push(this.loadWasmModule(def.type));
      }
    });

    try {
      await Promise.all(loadPromises);
      console.log('All WASM modules preloaded');
    } catch (error) {
      console.error('Error preloading WASM modules:', error);
      // Don't throw - individual module loading handles errors
    }
  }

  /**
   * Register the Audio Worklet processor
   * Loads the worklet module and registers it with the native AudioContext
   */
  private async registerAudioWorklet(): Promise<void> {
    if (!this.audioContext) {
      throw new Error('Audio context not initialized');
    }

    const workletUrl = '/audio-worklets/effects-processor.js';
    
    console.log('[EffectsManager] Registering worklet with native AudioContext:', {
      contextType: this.audioContext.constructor.name,
      hasAudioWorklet: 'audioWorklet' in this.audioContext,
      state: this.audioContext.state
    });

    // Check if audioWorklet is supported
    if (!this.audioContext.audioWorklet) {
      throw new Error('AudioWorklet is not supported in this browser');
    }

    try {
      // Register the audio worklet module using native AudioContext
      await this.audioContext.audioWorklet.addModule(workletUrl);
      console.log('Audio Worklet registered successfully with native AudioContext');
    } catch (error) {
      console.error('Failed to register Audio Worklet:', error);
      
      // Provide more helpful error messages
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('Not Found')) {
          throw new Error(`Audio Worklet file not found at ${workletUrl}. Ensure the file exists in the public directory.`);
        } else if (error.message.includes('syntax')) {
          throw new Error(`Audio Worklet has syntax errors. Check the console for details.`);
        }
      }
      
      throw new Error(`Failed to register Audio Worklet: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Create an AudioWorkletNode for a specific row
   * This node will process audio through the effects chain
   */
  async createWorkletNode(row: number): Promise<AudioWorkletNode | null> {
    if (!this.audioContext) {
      console.error('Audio context not initialized');
      throw new Error('Audio context not initialized. Call initialize() with an AudioContext first.');
    }

    // Ensure worklet is registered
    if (!this.isWorkletRegistered) {
      console.warn('Audio worklet not registered. Cannot create worklet node without initialization.');
      throw new Error('Audio worklet not registered. Call initialize() with an AudioContext first.');
    }

    // Dispose existing node if present
    if (this.workletNodes.has(row)) {
      console.log(`Disposing existing worklet node for row ${row}`);
      this.disposeWorkletNode(row);
    }

    try {
      console.log('[EffectsManager] Creating AudioWorkletNode with native AudioContext', {
        contextType: this.audioContext.constructor.name,
        state: this.audioContext.state,
        sampleRate: this.audioContext.sampleRate
      });
      
      // Create AudioWorkletNode using the native AudioContext directly
      const node = new AudioWorkletNode(
        this.audioContext,
        'effects-processor',
        {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          channelCount: 2,
          channelCountMode: 'explicit',
          channelInterpretation: 'speakers'
        }
      );
      
      console.log('[EffectsManager] Successfully created AudioWorkletNode with native AudioContext');

      // Set up message handler for worklet responses
      node.port.onmessage = (event) => {
        this.handleWorkletMessage(row, event);
      };

      // Set up error handler
      node.onprocessorerror = (event) => {
        console.error(`Processor error for row ${row}:`, event);
        this.handleWorkletError(row, event);
      };

      // Store the node
      this.workletNodes.set(row, node);

      console.log(`Created worklet node for row ${row}`);
      return node;
    } catch (error) {
      console.error(`Failed to create worklet node for row ${row}:`, error);
      throw error;
    }
  }

  /**
   * Initialize a worklet node with WASM module
   * Sends the compiled WASM module to the worklet for processing
   */
  async initializeWorkletWithWasm(
    row: number,
    effectTypes: EffectType[]
  ): Promise<void> {
    const node = this.workletNodes.get(row);
    if (!node) {
      console.error(`No worklet node found for row ${row}`);
      throw new Error(`No worklet node found for row ${row}`);
    }

    // Load WASM modules for all effect types in the chain
    const uniqueTypes = [...new Set(effectTypes)].filter(type => type !== 'none');
    
    if (uniqueTypes.length === 0) {
      console.log(`No WASM modules needed for row ${row}`);
      return;
    }

    let hasWasmInitialized = false;
    
    for (const effectType of uniqueTypes) {
      try {
        // First, try to load and cache the WASM module
        const wasmModule = await this.loadWasmModule(effectType);
        
        if (wasmModule) {
          // Fetch the WASM binary to send to worklet
          // We need the ArrayBuffer, not the compiled Module
          const effectDef = this.getEffectDefinition(effectType);
          if (effectDef?.wasmModule) {
            const response = await fetch(effectDef.wasmModule);
            
            if (!response.ok) {
              throw new Error(`Failed to fetch WASM module: ${response.statusText}`);
            }
            
            const moduleBuffer = await response.arrayBuffer();
            
            // Send WASM module to worklet
            node.port.postMessage({
              type: 'init-wasm',
              data: {
                module: moduleBuffer,
                memory: null // Let worklet create its own memory
              }
            });

            hasWasmInitialized = true;
            console.log(`Initialized worklet for row ${row} with WASM module: ${effectType}`);
          }
        } else {
          console.warn(`WASM module not available for ${effectType}, will use fallback`);
        }
      } catch (error) {
        console.error(`Failed to initialize WASM for ${effectType}:`, error);
        
        // Continue to next effect type, fallback will be used for this one
      }
    }

    // If no WASM modules were successfully initialized, enable fallback mode
    if (!hasWasmInitialized) {
      console.log(`No WASM modules initialized for row ${row}, enabling fallback mode`);
      node.port.postMessage({
        type: 'enable-fallback'
      });
    }
  }

  /**
   * Handle messages from the worklet
   */
  private handleWorkletMessage(_row: number, event: MessageEvent): void {
    const { type, error, context } = event.data;
    const row = _row;

    switch (type) {
      case 'ready':
        console.log(`Worklet ready for row ${row}`);
        break;

      case 'wasm-initialized':
        console.log(`WASM initialized for row ${row}`);
        break;

      case 'wasm-init-failed':
        console.warn(`WASM initialization failed for row ${row}:`, error);
        // Worklet will automatically fall back to JavaScript implementation
        break;

      case 'fallback-enabled':
        console.log(`Fallback mode enabled for row ${row}`);
        break;

      case 'chain-updated':
        console.log(`Effects chain updated for row ${row}`);
        break;

      case 'error':
        console.error(`Worklet error for row ${row} (${context}):`, error);
        break;

      default:
        console.log(`Unknown message from worklet for row ${row}:`, type);
    }
  }

  /**
   * Handle worklet processor errors
   */
  private handleWorkletError(row: number, event: Event): void {
    console.error(`Audio worklet processor error for row ${row}:`, event);
    
    // Optionally, we could try to recreate the worklet node
    // or notify the UI that effects processing has failed
    // For now, just log the error
  }

  /**
   * Get the worklet node for a specific row
   */
  getWorkletNode(row: number): AudioWorkletNode | undefined {
    return this.workletNodes.get(row);
  }

  /**
   * Create an effect instance with a unique ID
   */
  createEffectInstance(
    type: EffectType,
    slot: number,
    parameters?: Record<string, number>
  ): EffectInstance {
    const effectDef = this.getEffectDefinition(type);
    
    // Generate unique ID
    const id = `${type}-${slot}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    
    // Use provided parameters or defaults from definition
    const defaultParams: Record<string, number> = {};
    if (effectDef) {
      effectDef.parameters.forEach(param => {
        defaultParams[param.id] = param.default;
      });
    }

    return {
      id,
      type,
      parameters: parameters || defaultParams,
      bypassed: false,
      slot
    };
  }

  /**
   * Update the effects chain for a specific row
   * Sends the updated chain to the worklet for processing
   */
  async updateEffectsChain(row: number, effects: EffectInstance[]): Promise<void> {
    let node = this.workletNodes.get(row);
    
    if (!node) {
      console.warn(`No worklet node found for row ${row}, creating one`);
      await this.createWorkletNode(row);
      node = this.workletNodes.get(row);
      if (!node) {
        console.error(`Failed to create worklet node for row ${row}`);
        return;
      }
    }

    const workletNode = node;

    // Initialize WASM modules for new effect types
    const effectTypes = effects.map(e => e.type);
    await this.initializeWorkletWithWasm(row, effectTypes);

    // Send updated chain to worklet
    workletNode.port.postMessage({
      type: 'update-chain',
      data: {
        effects: effects.map(effect => ({
          id: effect.id,
          type: effect.type,
          parameters: effect.parameters,
          bypassed: effect.bypassed,
          slot: effect.slot
        }))
      }
    });

    // IMPORTANT: Send initial parameter values for each effect immediately after updating chain
    // This ensures effects are properly initialized and process audio right away
    effects.forEach(effect => {
      if (effect.type !== 'none' && effect.parameters) {
        Object.entries(effect.parameters).forEach(([parameterId, value]) => {
          workletNode.port.postMessage({
            type: 'update-parameter',
            data: {
              effectId: effect.id,
              parameterId,
              value
            }
          });
        });
      }
    });

    console.log(`Updated effects chain for row ${row}:`, effects);
  }

  /**
   * Add an effect to the chain at a specific slot
   */
  async addEffect(
    row: number,
    slot: number,
    type: EffectType,
    currentChain: EffectInstance[]
  ): Promise<EffectInstance[]> {
    // Create new effect instance
    const newEffect = this.createEffectInstance(type, slot);

    // Create updated chain
    const updatedChain = [...currentChain];
    
    // Find if there's already an effect in this slot
    const existingIndex = updatedChain.findIndex(e => e.slot === slot);
    
    if (existingIndex >= 0) {
      // Replace existing effect in slot
      updatedChain[existingIndex] = newEffect;
    } else {
      // Add new effect and sort by slot
      updatedChain.push(newEffect);
      updatedChain.sort((a, b) => a.slot - b.slot);
    }

    // Update the worklet
    await this.updateEffectsChain(row, updatedChain);

    return updatedChain;
  }

  /**
   * Remove an effect from the chain
   */
  async removeEffect(
    row: number,
    effectId: string,
    currentChain: EffectInstance[]
  ): Promise<EffectInstance[]> {
    // Filter out the effect
    const updatedChain = currentChain.filter(e => e.id !== effectId);

    // Update the worklet if it exists
    const node = this.workletNodes.get(row);
    if (node) {
      await this.updateEffectsChain(row, updatedChain);
    } else {
      console.warn(`No worklet node found for row ${row}, effect removed from chain only`);
    }

    return updatedChain;
  }

  /**
   * Reorder effects in the chain
   */
  async reorderEffects(
    row: number,
    effects: EffectInstance[]
  ): Promise<void> {
    // Update slot numbers to match new order
    const reorderedEffects = effects.map((effect, index) => ({
      ...effect,
      slot: index
    }));

    // Update the worklet
    await this.updateEffectsChain(row, reorderedEffects);
  }

  /**
   * Clear all effects from a row
   */
  async clearEffectsChain(row: number): Promise<void> {
    await this.updateEffectsChain(row, []);
  }

  /**
   * Update a parameter for a specific effect
   * Validates parameter range and sends update to worklet
   */
  updateEffectParameter(
    row: number,
    effectId: string,
    parameterId: string,
    value: number,
    currentChain: EffectInstance[]
  ): EffectInstance[] {
    // Find the effect in the chain
    const effect = currentChain.find(e => e.id === effectId);
    
    if (!effect) {
      console.warn(`Effect ${effectId} not found in chain for row ${row}`);
      return currentChain;
    }

    // Get effect definition for validation
    const effectDef = this.getEffectDefinition(effect.type);
    const paramDef = effectDef?.parameters.find(p => p.id === parameterId);

    // Validate parameter value
    let validatedValue = value;
    if (paramDef) {
      validatedValue = Math.max(paramDef.min, Math.min(paramDef.max, value));
      
      // Round to step if specified
      if (paramDef.step) {
        validatedValue = Math.round(validatedValue / paramDef.step) * paramDef.step;
      }
    }

    // Update parameter in chain
    const updatedChain = currentChain.map(e => {
      if (e.id === effectId) {
        return {
          ...e,
          parameters: {
            ...e.parameters,
            [parameterId]: validatedValue
          }
        };
      }
      return e;
    });

    // Send update to worklet with debouncing
    this.sendParameterUpdateDebounced(row, effectId, parameterId, validatedValue);

    return updatedChain;
  }

  /**
   * Send parameter update to worklet with debouncing
   * Debounces rapid parameter changes to reduce message overhead
   */
  private sendParameterUpdateDebounced(
    row: number,
    effectId: string,
    parameterId: string,
    value: number,
    debounceMs: number = 16 // ~60fps
  ): void {
    const node = this.workletNodes.get(row);
    
    if (!node) {
      console.warn(`No worklet node found for row ${row}`);
      return;
    }

    // Create unique key for this parameter update
    const key = `${row}-${effectId}-${parameterId}`;

    // Clear existing timer for this parameter
    const existingTimer = this.parameterDebounceTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer
    const timer = setTimeout(() => {
      node.port.postMessage({
        type: 'update-parameter',
        data: {
          effectId,
          parameterId,
          value
        }
      });

      // Remove timer from map
      this.parameterDebounceTimers.delete(key);
    }, debounceMs);

    this.parameterDebounceTimers.set(key, timer);
  }

  /**
   * Update multiple parameters at once (no debouncing)
   * Useful for loading presets or initializing effects
   */
  updateEffectParameters(
    row: number,
    effectId: string,
    parameters: Record<string, number>,
    currentChain: EffectInstance[]
  ): EffectInstance[] {
    // Find the effect in the chain
    const effect = currentChain.find(e => e.id === effectId);
    
    if (!effect) {
      console.warn(`Effect ${effectId} not found in chain for row ${row}`);
      return currentChain;
    }

    // Get effect definition for validation
    const effectDef = this.getEffectDefinition(effect.type);

    // Validate all parameters
    const validatedParams: Record<string, number> = {};
    Object.entries(parameters).forEach(([paramId, value]) => {
      const paramDef = effectDef?.parameters.find(p => p.id === paramId);
      
      if (paramDef) {
        let validatedValue = Math.max(paramDef.min, Math.min(paramDef.max, value));
        
        if (paramDef.step) {
          validatedValue = Math.round(validatedValue / paramDef.step) * paramDef.step;
        }
        
        validatedParams[paramId] = validatedValue;
      } else {
        validatedParams[paramId] = value;
      }
    });

    // Update parameters in chain
    const updatedChain = currentChain.map(e => {
      if (e.id === effectId) {
        return {
          ...e,
          parameters: {
            ...e.parameters,
            ...validatedParams
          }
        };
      }
      return e;
    });

    // Send all parameter updates to worklet at once (if worklet exists)
    const node = this.workletNodes.get(row);
    if (node) {
      Object.entries(validatedParams).forEach(([paramId, value]) => {
        node.port.postMessage({
          type: 'update-parameter',
          data: {
            effectId,
            parameterId: paramId,
            value
          }
        });
      });
    } else {
      console.warn(`No worklet node found for row ${row}, parameter updates stored in chain only`);
    }

    return updatedChain;
  }

  /**
   * Toggle bypass state for an effect
   * When bypassed, the effect is skipped in the processing chain
   */
  toggleEffectBypass(
    row: number,
    effectId: string,
    bypassed: boolean,
    currentChain: EffectInstance[]
  ): EffectInstance[] {
    // Update bypass state in chain
    const updatedChain = currentChain.map(e => {
      if (e.id === effectId) {
        return {
          ...e,
          bypassed
        };
      }
      return e;
    });

    // Send bypass update to worklet (if worklet exists)
    const node = this.workletNodes.get(row);
    if (node) {
      node.port.postMessage({
        type: 'bypass-effect',
        data: {
          effectId,
          bypassed
        }
      });
      console.log(`Effect ${effectId} bypass set to ${bypassed} for row ${row}`);
    } else {
      console.warn(`No worklet node found for row ${row}, bypass state stored in chain only`);
    }

    return updatedChain;
  }

  /**
   * Bypass all effects in a chain
   */
  bypassAllEffects(
    row: number,
    bypassed: boolean,
    currentChain: EffectInstance[]
  ): EffectInstance[] {
    // Update bypass state for all effects
    const updatedChain = currentChain.map(e => ({
      ...e,
      bypassed
    }));

    // Send bypass updates to worklet for each effect (if worklet exists)
    const node = this.workletNodes.get(row);
    if (node) {
      updatedChain.forEach(effect => {
        node.port.postMessage({
          type: 'bypass-effect',
          data: {
            effectId: effect.id,
            bypassed
          }
        });
      });
      console.log(`All effects ${bypassed ? 'bypassed' : 'enabled'} for row ${row}`);
    } else {
      console.warn(`No worklet node found for row ${row}, bypass states stored in chain only`);
    }

    return updatedChain;
  }

  /**
   * Remove an effect from a specific slot
   * This is an alias for removeEffect but more explicit about slot-based removal
   */
  removeEffectFromSlot(
    row: number,
    slot: number,
    currentChain: EffectInstance[]
  ): Promise<EffectInstance[]> {
    const effect = currentChain.find(e => e.slot === slot);
    
    if (!effect) {
      console.warn(`No effect found in slot ${slot} for row ${row}`);
      return Promise.resolve(currentChain);
    }

    return this.removeEffect(row, effect.id, currentChain);
  }

  /**
   * Check if a row has any active (non-bypassed) effects
   */
  hasActiveEffects(_row: number, currentChain: EffectInstance[]): boolean {
    return currentChain.some(e => !e.bypassed && e.type !== 'none');
  }

  /**
   * Get effect instance by ID
   */
  getEffectById(effectId: string, currentChain: EffectInstance[]): EffectInstance | undefined {
    return currentChain.find(e => e.id === effectId);
  }

  /**
   * Get effect instance by slot
   */
  getEffectBySlot(slot: number, currentChain: EffectInstance[]): EffectInstance | undefined {
    return currentChain.find(e => e.slot === slot);
  }

  /**
   * Dispose worklet node for a specific row
   */
  private disposeWorkletNode(row: number): void {
    const node = this.workletNodes.get(row);
    if (node) {
      try {
        node.disconnect();
        node.port.postMessage({ type: 'dispose' });
      } catch (error) {
        console.error(`Error disposing worklet node for row ${row}:`, error);
      }
      this.workletNodes.delete(row);
    }
  }
}
